import { Container } from '@/components/Container';
import { useTranslation } from '@/hooks/useTranslation';
import { useAffiliate } from '@/hooks/useAffiliate';
import { ExternalLink } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';

/* ═══════════════════════════════════════════
   ONDE FICAR — Comparativo Nova Brasília × Encantadas
   As pousadas individuais vivem na pilar /pousadas-ilha-do-mel
   ═══════════════════════════════════════════ */

export function OndeFicarSection() {
  const { t } = useTranslation();
  const { generateLink } = useAffiliate();
  const { trackAffiliate } = useAnalytics();

  const comparativo = [
    {
      label: t('guide.ondeFicar.compare.0.label'),
      nb: t('guide.ondeFicar.compare.0.nb'),
      enc: t('guide.ondeFicar.compare.0.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.1.label'),
      nb: t('guide.ondeFicar.compare.1.nb'),
      enc: t('guide.ondeFicar.compare.1.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.2.label'),
      nb: t('guide.ondeFicar.compare.2.nb'),
      enc: t('guide.ondeFicar.compare.2.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.3.label'),
      nb: t('guide.ondeFicar.compare.3.nb'),
      enc: t('guide.ondeFicar.compare.3.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.4.label'),
      nb: t('guide.ondeFicar.compare.4.nb'),
      enc: t('guide.ondeFicar.compare.4.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.5.label'),
      nb: t('guide.ondeFicar.compare.5.nb'),
      enc: t('guide.ondeFicar.compare.5.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.6.label'),
      nb: t('guide.ondeFicar.compare.6.nb'),
      enc: t('guide.ondeFicar.compare.6.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.7.label'),
      nb: t('guide.ondeFicar.compare.7.nb'),
      enc: t('guide.ondeFicar.compare.7.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.8.label'),
      nb: t('guide.ondeFicar.compare.8.nb'),
      enc: t('guide.ondeFicar.compare.8.enc'),
    },
    {
      label: t('guide.ondeFicar.compare.9.label'),
      nb: t('guide.ondeFicar.compare.9.nb'),
      enc: t('guide.ondeFicar.compare.9.enc'),
    },
  ];

  return (
    <section id="onde-ficar" className="py-16 sm:py-20 bg-white">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.ondeFicar.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.ondeFicar.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.ondeFicar.subtitle')}
          </p>
        </div>

        {/* Dica de ouro */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 mb-12 max-w-2xl mx-auto">
          <span className="text-2xl">💡</span>
          <div>
            <p className="font-body text-sm font-semibold text-warm-dark">
              {t('guide.ondeFicar.tipTitle')}
            </p>
            <p className="font-body text-sm text-warm-gray">{t('guide.ondeFicar.tipText')}</p>
          </div>
        </div>

        {/* Tabela comparativa */}
        <div className="mb-16 overflow-x-auto">
          <h3 className="font-display text-xl text-warm-dark mb-4 text-center">
            {t('guide.ondeFicar.compareTitle')}
          </h3>
          <table className="w-full font-body text-sm">
            <thead>
              <tr className="border-b-2 border-sand-200">
                <th className="text-left py-3 px-4 font-semibold text-warm-dark bg-sand-50 rounded-tl-lg">
                  {t('guide.ondeFicar.criteriaHeader')}
                </th>
                <th className="text-left py-3 px-4 font-semibold text-teal-deep bg-teal-50">
                  {t('guide.ondeFicar.nbHeader')}
                </th>
                <th className="text-left py-3 px-4 font-semibold text-coral bg-coral/5 rounded-tr-lg">
                  {t('guide.ondeFicar.encHeader')}
                </th>
              </tr>
            </thead>
            <tbody>
              {comparativo.map((c, i) => (
                <tr
                  key={c.label}
                  className={`border-b border-sand-100 ${i % 2 === 0 ? 'bg-white' : 'bg-sand-50/50'}`}
                >
                  <td className="py-3 px-4 font-semibold text-warm-dark">{c.label}</td>
                  <td className="py-3 px-4 text-warm-gray">{c.nb}</td>
                  <td className="py-3 px-4 text-warm-gray">{c.enc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Card afiliado Booking.com */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl">🏨</span>
              <div>
                <h3 className="font-display text-xl text-warm-dark mb-1">
                  {t('guide.ondeFicar.affiliate.booking.title', 'Ver mais opções de hospedagem')}
                </h3>
                <p className="font-body text-sm text-warm-gray">
                  {t(
                    'guide.ondeFicar.affiliate.booking.subtitle',
                    'Compare preços e avaliações no Booking.com'
                  )}
                </p>
              </div>
            </div>
            <a
              href={generateLink('booking', 'onde-ficar', { ss: 'Ilha do Mel' })}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() =>
                trackAffiliate({
                  provider: 'Booking.com',
                  platform: 'booking',
                  productType: 'hotel',
                })
              }
              className="inline-flex items-center gap-2 bg-coral hover:bg-[#c96a52] text-white font-body font-semibold rounded-xl px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-coral/30 uppercase tracking-wide text-sm"
            >
              {t('guide.ondeFicar.affiliate.booking.cta', 'Buscar no Booking.com')}
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="font-body text-xs text-warm-gray mt-3">
              {t('guide.affiliateBadge', 'Link afiliado — você não paga mais por isso')}
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}
